import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import logoText from '../../assets/images/logo_white_text.png';
import { blogPosts } from '../../data/blogPosts';

const QUICK_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about' },
  { label: 'All Services', to: '/services' },
  { label: 'Pet Care Blog', to: '/blog' },
  { label: 'TailTalks Community', to: '/tailtalk' },
  { label: 'Contact', to: '/contact' },
  { label: 'Careers', to: '/careers' },
];

const SERVICE_LINKS = [
  { label: 'Online Vet Consultation', to: '/online-vet-consultation' },
  { label: 'PawDoc AI Vet (Free 24/7)', to: '/ai-vet' },
  { label: 'Pet Grooming', to: '/services/grooming' },
  { label: 'Pet Boarding', to: '/services/boarding' },
  { label: 'Pet Transportation', to: '/services/transportation' },
  { label: 'Pet Diet Plans', to: '/services/diet-plan' },
  { label: 'Adopt a Pet', to: '/find-furry-soulmate' },
  { label: 'Pet Rehoming', to: '/rehome' },
];

const SHOP_LINKS = [
  { label: 'Dog Food', to: '/products?category=Food&petType=Dog' },
  { label: 'Cat Food', to: '/products?category=Food&petType=Cat' },
  { label: 'Pet Toys & Accessories', to: '/products?category=Accessories' },
  { label: 'Pet Medicines & Health', to: '/products?category=Health' },
  { label: 'Featured Products', to: '/products?featured=true' },
  { label: 'All Pet Products', to: '/products' },
];

const POPULAR_SEARCH_TERMS = [
  'Dog food', 'Cat food', 'Puppy food', 'Royal Canin', 'Pedigree', 'Cat litter',
  'Dog toys', 'Dog shampoo', 'Dog leash', 'Cat treats', 'Dog collar', 'Pet supplements',
];

const SOCIAL_LINKS = [
  { label: 'Petzify on Facebook', href: 'https://www.facebook.com/profile.php?id=61578457112626', Icon: FaFacebook },
  { label: 'Petzify on X (Twitter)', href: 'https://x.com/petzifyofficial', Icon: FaXTwitter },
  { label: 'Petzify on Instagram', href: 'https://www.instagram.com/petzify', Icon: FaInstagram },
  { label: 'Petzify on LinkedIn', href: 'https://www.linkedin.com/company/petzifyinofficial/', Icon: FaLinkedin },
];

const footerLinkClass =
  'hover:text-white text-white/85 transition-colors duration-300 flex items-center group text-sm';

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className={footerLinkClass}>
      <span className="w-0 h-0.5 bg-secondary-light mr-0 transition-all duration-300 group-hover:w-2 group-hover:mr-2"></span>
      {children}
    </Link>
  </li>
);

const Footer = () => {
  // Newest six guides — includes the service-focused articles
  const featuredArticles = blogPosts.slice(-6).reverse();

  return (
    <footer className="bg-gradient-to-r from-primary-dark to-primary text-white pt-12 pb-24 md:pb-12 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute w-96 h-96 rounded-full bg-white opacity-5 -top-40 -left-40"></div>
        <div className="absolute w-64 h-64 rounded-full bg-white opacity-5 bottom-0 right-20"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-4">
              <img src={logoText} alt="Petzify" className="h-9 w-auto" loading="lazy" width="140" height="36" />
            </div>
            <p className="mb-4 text-sm text-white/90 leading-relaxed">
              Petzify is India's complete pet care platform — buy dog food, cat food, toys,
              accessories and pet medicines online, consult certified veterinarians, chat free with
              PawDoc AI, and book pet grooming, boarding, transportation and personalized diet
              plans across India.
            </p>
            <div className="flex space-x-4">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="hover:text-secondary-light transition-all duration-300 transform hover:scale-125"
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg font-bold mb-4 relative inline-block">
              Quick Links
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary-light"></div>
            </h3>
            <ul className="space-y-2">
              {QUICK_LINKS.map((link) => (
                <FooterLink key={link.label} to={link.to}>{link.label}</FooterLink>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4 relative inline-block">
              Pet Services
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary-light"></div>
            </h3>
            <ul className="space-y-2">
              {SERVICE_LINKS.map((link) => (
                <FooterLink key={link.label} to={link.to}>{link.label}</FooterLink>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-lg font-bold mb-4 relative inline-block">
              Shop Online
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary-light"></div>
            </h3>
            <ul className="space-y-2">
              {SHOP_LINKS.map((link) => (
                <FooterLink key={link.label} to={link.to}>{link.label}</FooterLink>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-4 relative inline-block">
              Contact Us
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary-light"></div>
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start">
                <span className="text-secondary-light mr-2 mt-0.5" aria-hidden="true">📍</span>
                <span>Kochi, Kerala, India</span>
              </li>
              <li className="flex items-center">
                <span className="text-secondary-light mr-2" aria-hidden="true">📞</span>
                <a href="tel:+919497672523" className="hover:text-secondary-light transition-colors duration-300">
                  +91 94976 72523
                </a>
              </li>
              <li className="flex items-center">
                <span className="text-secondary-light mr-2" aria-hidden="true">✉️</span>
                <a href="mailto:support@petzify.in" className="hover:text-secondary-light transition-colors duration-300">
                  support@petzify.in
                </a>
              </li>
              <li className="flex items-center">
                <span className="text-secondary-light mr-2" aria-hidden="true">💼</span>
                <a
                  href="https://www.linkedin.com/company/petzifyinofficial/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-secondary-light transition-colors duration-300"
                >
                  @petzifyinofficial
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Pet care guides - internal links to keyword-rich articles */}
        <div className="border-t border-white border-opacity-20 mt-10 pt-8">
          <h3 className="text-lg font-bold mb-4">Pet Care Guides</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {featuredArticles.map((post) => (
              <li key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="text-sm text-white/85 hover:text-white hover:underline transition-colors duration-300 line-clamp-1"
                >
                  {post.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Popular searches - high-intent product queries */}
        <div className="border-t border-white border-opacity-20 mt-8 pt-8">
          <h3 className="text-lg font-bold mb-4">Popular Searches</h3>
          <div className="flex flex-wrap gap-2">
            {POPULAR_SEARCH_TERMS.map((term) => (
              <Link
                key={term}
                to={`/products?search=${encodeURIComponent(term.toLowerCase())}`}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white/90 hover:text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200"
              >
                {term}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white border-opacity-20 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-sm">
                © {new Date().getFullYear()} Petzify — India's Pet Care Platform. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end space-x-6">
              <Link
                to="/privacy-policy"
                className="hover:text-secondary-light transition-colors duration-300 text-sm"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                className="hover:text-secondary-light transition-colors duration-300 text-sm"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/return-policy"
                className="hover:text-secondary-light transition-colors duration-300 text-sm"
              >
                Return Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
